import { CircleCheck, CircleX } from "lucide-react";
import { createCheckoutSession } from "../../../utils/APIs";
import { useUserContext } from "../../../contexts/UserContext";
import { useState } from "react";
import EmbeddedCheckoutComponent from "../../../components/Stripe/EmbeddedCheckout";

export default function PricingCard({ key, plan, billingCycle, isEnterprise = false, index }) {
    const { userData } = useUserContext();
    const [submitting, setSubmitting] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);

    const handleCheckout = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);

            const data = {
                plan_id: plan._id,
                ui_mode: 'embedded',
                metadata: {
                    userId: userData?.user_id || "",
                    customerEmail: userData?.email || "",
                    plan: plan.plan_name,
                    plan_id: plan._id,
                },
            };

            const res = await createCheckoutSession(
                userData.token,
                userData?.selectedOrganization?.org_token,
                data
            );
            if (res?.data?.success && res?.data?.clientSecret) {
                setClientSecret(res.data.clientSecret);
                setShowCheckout(true);
            }

        } catch (err) {
            console.error("Subscription error:", err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div key={key} className="flex flex-col items-center h-full bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
            {/* Header */}
            <div className="mb-6">
                {/* Plan Name */}
                <h3 className="text-[14px] font-bold capitalize text-[#121933C2] mb-2">
                    {plan.plan_name}
                </h3>

                {/* Display Price */}
                <div className="flex items-baseline gap-1">
                    {plan.plan_name.toLowerCase().includes("enterprise") ? (
                        <span className="text-[40px] font-bold text-[#465DAE]">Let's Talk</span>
                    ) : typeof plan.price === "number" ? (
                        plan.price === 0 ? (
                            <span className="text-[40px] font-bold text-[#465DAE]">Free</span>
                        ) : (
                            <>
                                <span className="text-[40px] font-bold text-[#465DAE]">
                                    {plan.price_currency === "EUR" ? "€" : "$"}
                                    {plan.price}
                                </span>
                                    {plan.is_topup ? (
                                        <span className="text-gray-600 text-sm">
                                            till plan ends
                                        </span>
                                    ) : (
                                        <span className="text-gray-600 text-sm">
                                            {billingCycle.toLowerCase().includes("month") ? "/ month" : "/ yearly"}
                                        </span>
                                    )}
                            </>
                        )
                    ) : (
                        <span className="text-[40px] font-bold text-[#465DAE]">
                            {plan.price}
                        </span>
                    )}
                </div>

            </div>

            {/* Features List */}
            <div className="flex-1 mb-6">
                <ul className="space-y-4">
                    {plan.features?.map((feature, index) => (
                        <li key={index} className="flex items-center justify-between gap-3">
                            <span
                                className={`text-medium ${feature.included ? "text-gray-700" : "text-gray-400"
                                    }`}
                            >
                                {feature.name}
                            </span>
                            <div className="flex-shrink-0">
                                {feature.included ? (
                                    <CircleCheck className="w-5 h-5 text-[#465DAE]" />
                                ) : (
                                    <CircleX className="w-5 h-5 text-gray-300" />
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Purchase Button */}
            {!isEnterprise ? (
                <button
                    id={`purchase-plan-btn-${index}`}
                    onClick={handleCheckout}
                    disabled={submitting}
                    className={`w-full py-2.5 px-4 rounded-full font-medium transition-colors border-2 border-[#465DAE] text-[#465DAE] bg-transparent hover:bg-[#465DAE]/5 ${submitting ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                >
                    {submitting ? "Processing..." : plan.buttonText || "Purchase"}
                </button>
            ) : (
                <button
                    onClick={() => window.open('https://stridetech.io/book-a-demo/', '_blank')}
                    className="w-full py-2.5 px-4 rounded-full font-medium text-white bg-[#465DAE] hover:opacity-90"
                >
                    {plan.buttonText || "Book a Demo"}
                </button>
            )}

            {showCheckout && (
                <EmbeddedCheckoutComponent
                    clientSecret={clientSecret}
                    onCancel={() => {
                        setShowCheckout(false);
                        setClientSecret(null);
                    }}
                />
            )}
        </div>
    );
}
